import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPartnerDashboard } from "../../api/partnerApi";
import { useAuth } from "../../auth/useAuth";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";

const PartnerDashboardPage = () => {
  const { getPrimaryPartnerOrganization } = useAuth();
  const primary = getPrimaryPartnerOrganization();
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        const body = await getPartnerDashboard();
        if (!cancelled) {
          setData(body.data);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(error.message || "Unable to load dashboard.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading) {
    return <p className="muted panel-card">Loading dashboard…</p>;
  }

  if (errorMessage) {
    return (
      <section className="panel-card">
        <p className="admin-data-health-error">{errorMessage}</p>
      </section>
    );
  }

  const orgName =
    primary?.partnerName ||
    data?.partnerOrganizations?.[0]?.partnerName ||
    "Your organization";

  return (
    <>
      <section className="panel-card">
        <h2>{orgName}</h2>
        <p className="muted">Partner-scoped summary for your assigned data only.</p>
      </section>

      <section className="panel-card partner-dashboard-cards">
        <div className="partner-stat-card">
          <span className="muted">Total listings</span>
          <strong>{data?.listingCount ?? 0}</strong>
        </div>
        <div className="partner-stat-card">
          <span className="muted">Active</span>
          <strong>{data?.activeListingCount ?? 0}</strong>
        </div>
        <div className="partner-stat-card">
          <span className="muted">Inactive / stale</span>
          <strong>{data?.disabledListingCount ?? 0}</strong>
        </div>
        <div className="partner-stat-card">
          <span className="muted">Sources</span>
          <strong>{data?.sourceCount ?? 0}</strong>
        </div>
        {data?.lastSourceRunAt ? (
          <p className="muted">Last source run: {formatDateTime(data.lastSourceRunAt)}</p>
        ) : null}
      </section>

      <section className="panel-card">
        <div className="account-security-section__header">
          <h2>Recent listings</h2>
          <Link to="/partner/listings">View all</Link>
        </div>
        {(data?.recentListings || []).length ? (
          <table className="admin-data-health-table">
            <thead>
              <tr>
                <th>Address</th>
                <th>Price</th>
                <th>Status</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {data.recentListings.map((listing) => (
                <tr key={listing.listingId}>
                  <td>{listing.addressLine || listing.city || "—"}</td>
                  <td>
                    {listing.price != null
                      ? `${listing.currency || ""} ${listing.price}`.trim()
                      : "—"}
                  </td>
                  <td>{listing.status || "—"}</td>
                  <td>{listing.sourceDisplayName || listing.sourceCode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No listings yet for your partner scope.</p>
        )}
      </section>

      <section className="panel-card">
        <div className="account-security-section__header">
          <h2>Source health</h2>
          <Link to="/partner/sources">View sources</Link>
        </div>
        {(data?.sourceHealth || []).length ? (
          <ul className="account-sign-in-methods">
            {data.sourceHealth.map((source) => (
              <li key={source.sourceKey}>
                <strong>{source.displayName}</strong>
                <span className="muted">
                  {source.enabled ? "Enabled" : "Disabled"} · {source.lastStatus || "—"}
                  {source.lastRunAt ? ` · ${formatDateTime(source.lastRunAt)}` : ""}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No sources linked to your partner organizations.</p>
        )}
      </section>
    </>
  );
};

export default PartnerDashboardPage;
